import type { StringKey } from '../strings';
import type { PagedRecord } from './recordPages';
import { PAGE_SETTINGS_ENTRIES, type PageSettingsEntry } from './pageSettings';

/**
 * 🔒 The record page's per-type facts, as data beside the tiles (T180). The
 * product owner, 2026-09-22: *"Id prefer they be more function since the change
 * to one propagates opposed to us having to check each one."*
 *
 * 🔨 The record page branches on no record type. Which modals a page mounts,
 * which panel replaces which tile's rows, which header actions it offers and
 * whether it is a staged layout are facts about the type, declared here beside
 * its tiles, its edit fields and its icon. The screen reads the fact.
 *
 * 🔨 A React component is named here by key and never imported: this package is
 * DOM-free (decision 0242), and one map in the record page resolves a key to the
 * component that draws it. A key with no entry in that map refuses to compile.
 */

/** The modals a record page mounts beyond the ones every record page carries. */
export type RecordModalKey =
  /** The Site page's own windows (piece 13; decisions 0295, 0309, 0676). */
  | 'siteCertificate'
  | 'siteRequirement'
  | 'siteRole'
  | 'siteRequirementEnd'
  /** Decision 0676: the dialog that ends a certificate, asked from its row. */
  | 'siteCertificateEnd'
  | 'siteCertificateRemove'
  | 'siteCertificateMove'
  /** 🔒 Decision 2851: the Exempt Customer Steps window, opened from the certificates tile's header. */
  | 'siteExemptSteps'
  /** The Item page's own four (piece 17; decisions 0269, 0296). */
  | 'itemAttribute'
  | 'itemSupplier'
  | 'itemSiteStock'
  | 'itemImage'
  /** 🔒 Issue Stock, opened from under the Stock by Site tile (decision 1142). */
  | 'itemIssueStock'
  /** An occupancy corrected or ended from the tile that draws it, on the Company page and the Location page alike (decision 0589). */
  | 'siteOccupancy'
  /** + Add Occupant on a Location, which writes a site into the building being read (piece 13). */
  | 'locationOccupant'
  /** The picture the banner's own slot draws, settable on a Person and a Company alone (T170; decision 0616). */
  | 'recordPicture'
  /** An employment corrected from the tile that draws it, on the Person page and the Company page alike (decision 0589). */
  | 'employmentEdit'
  /** A new employment, written from whichever end the page is (decision 0165). */
  | 'employmentAdd'
  /** The Person page's own three (decisions 0464, 0593, 0614, 6400). */
  | 'personSkill'
  | 'personContactMethod'
  /** 🔒 + Add School, the page's own window as the Employment window is, so a folded Education tile still opens it (decision 6400). */
  | 'personEducation'
  /** 🔒 T2352: + Grant Role, the page's own window, so a folded Confidentiality Roles tile still opens it. */
  | 'personConfidentialityGrant'
  /** The Company page's own windows (pieces 9, 10, 13; decisions 0158, 0165); + New Deal opens a page instead (decision 2510). */
  | 'companyNda'
  | 'companyAccountStanding'
  | 'companySite'
  | 'companyAccountRole'
  | 'companyVendorRep'
  /** 🔒 The client's terms set, from the Access & agreements tile (decision 1172). */
  | 'companyTermsSet'
  /** 🔒 Decision 2972: the Mailboxes tile's add and edit window. */
  | 'companyMailbox'
  /** 🔒 Decision 7501: General Information's + Add Owner and the End Ownership window an Owned By line or a Subsidiaries row opens. */
  | 'companyOwnership'
  /** The Deal page's own five (piece 19; decisions 0332, 0345, 0495). */
  | 'dealLost'
  | 'dealTeam'
  /** 🔒 Change Owner on the team panel, until the Deal is Won or Lost (decision 10701; T3618). */
  | 'dealOwner'
  | 'dealContact'
  | 'dealRfq'
  /** 🔒 The one Release window on a file row, mounted by the Deal, the Company and the Location (decision 11480). */
  | 'releaseFile'
  /** 🔒 Clone, its Account and what comes along (decision 1482). */
  | 'dealClone'
  /** 🔒 Delete Deal, which reads what the Deal holds when it opens (decision 12700). */
  | 'dealDelete'
  /** The Lead page's own four dialogs (piece 20). */
  | 'leadNotFit'
  | 'leadConvertDeal'
  | 'leadRedirect'
  | 'leadDispatch';

/**
 * A panel a record page draws in place of, beside or beneath a tile's own rows.
 * 🔨 The key is resolved to a component by one map on the record page, because
 * this package is DOM-free (decision 0242).
 */
export type RecordPanelKey =
  /** The Item's own image, beside its General Information fields (piece 17; decision 0269). */
  | 'itemImage'
  /** 🔒 Issue Stock under the Stock by Site tile's rows, for a writer of the Item (decision 1142). */
  | 'itemIssueStock'
  /** 🔒 The Item's Transactions tile, which reads its own route and draws its chips and table in place of rows (T1254; decision 1940). */
  | 'itemTransactions'
  /** 🔒 The Company's change requests, with Accept and Decline for the account owner and a Company writer (decision 0971). */
  | 'companyChangeRequests'
  /** Verify address, under the Location's own facts (decision 0338). */
  | 'locationVerify'
  /** 🔒 The Person's texts on the company number, read from their own route in place of the tile's rows (decision 5210). */
  | 'personTexts'
  /** 🔒 Decision 5382: the Suggestions tile on a Person and a Company, read from its own route with Accept and Dismiss. */
  | 'recordSuggestions'
  /** 🔒 Decision 6180: the Person's Education, Other Skills and Profile tiles, each read from its own route. */
  | 'personEducation'
  | 'personOtherSkills'
  | 'personProfile'
  /** 🔒 T2352: the Person's Confidentiality Roles tile, read from its own route with End on an open grant. */
  | 'personConfidentialityRoles'
  /** 🔒 The site's Tax Jurisdiction, which draws itself in place of a tile's rows (decision 0739). */
  | 'siteTaxJurisdiction'
  /** 🔒 Decision 12540: the trees standing at the site, the asset register's own tree and node page, in place of the Assets tile's rows. */
  | 'siteAssets'
  /** 🔒 Decision 12503: the site's Plant Feed tile, read from its own route. */
  | 'sitePlantFeed'
  /** 🔨 Piece 24: the Vendor Tile, revealed by the Is Vendor flag and gated by the route it reads. */
  | 'vendorTax'
  /** 🔒 Piece 29, decision 2080: the customer's message rules on its Field Service tile, read from their own route. */
  | 'companyJobMessages'
  /** 🔒 Piece 29, decision 2080: one Deal's message rules under its Work Orders tile, beside the customer's they override. */
  | 'dealJobMessages'
  /** The Lead's three StageCards, which draw themselves rather than a tile's item list (piece 20). */
  | 'leadStageCards'
  /** The Lead's ladder and the rail beside it, in the staged layout's panel (piece 20). */
  | 'leadProgress'
  | 'leadRail'
  /** The Deal's own constructions (piece 19; figures 81 to 83). */
  | 'dealProgress'
  | 'dealRail'
  | 'dealStageCard'
  | 'dealStages'
  /** The Deal's floor working, under General Information for a super admin alone (piece 19). */
  | 'dealFloorWorking'
  /** 🔒 The Deal banner's summary column: its value and its Next Action (decision 0665). */
  | 'dealBannerColumn'
  /** 🔒 Earlier Month at the foot of the Deal's History tile, the tile it pages (decision 1215). */
  | 'dealHistoryFoot'
  /** 🔒 The Deal's Next Action banner, in the Info tone at the head of the page (decision 1219). */
  | 'dealNextAction'
  /** The Work Order's own three constructions and its footer's controls (piece 22). */
  | 'workOrderLines'
  | 'workOrderMaterials'
  | 'workOrderTravel'
  | 'workOrderDealTeam'
  /** 🔒 The Field Service Path's Visits tile (decision 1908). */
  | 'workOrderVisits'
  /** 🔒 The Field Service Path's Labour tile (decision 1908). */
  | 'workOrderLabour'
  /** 🔒 The job's Documents tile: its visits' Service Reports and its invoices (decision 2910). */
  | 'workOrderDocuments'
  /** 🔒 Job Progress, the steps and the milestones, read-only (decision 6020). */
  | 'workOrderProgress'
  | 'workOrderFooter';

/**
 * 🔒 An action on the record page's banner, named by key and gated on the page's
 * own answer (T180). 🔨 The condition reads the facts the page already carries,
 * never the record type: Mark Lost stands on a deal that is neither won, lost nor
 * at its Decision stage, and that reading belongs beside the action.
 */
export type RecordActionKey =
  /** The Lead's own one-press moves (piece 20; decision 0277). */
  | 'leadCallAttempt'
  | 'leadConvertDeal'
  | 'leadConvertCompany'
  | 'leadRelease'
  | 'leadHot'
  | 'leadNotFit'
  | 'leadRedirect'
  | 'leadDispatch'
  | 'leadBlock'
  /** The suggested match, which is a row of its own beneath the banner's actions (piece 20). */
  | 'leadSuggestedMatch'
  /** The Company's own banner moves (pieces 13, 19; decisions 0223, 0497). */
  | 'companyStatements'
  | 'companyNewDeal'
  /** 🔒 An outside login's one control on its own company page (decision 0971). */
  | 'companyRequestChange'
  /** The Deal's own banner moves (piece 19; decisions 0353, 0595). */
  | 'dealLost'
  | 'dealReopen'
  | 'dealApprove'
  | 'dealReject'
  | 'dealGate'
  /** 🔒 Clone a Deal in any stage (decision 1482). */
  | 'dealClone'
  /** 🔒 The Deal Pack under More, for any reader of the Deal (decision 5400). */
  | 'dealCopyForClaude'
  | 'dealDownloadPack'
  /** 🔒 Delete Deal under More, for a writer of the Deal in every stage (decision 12700). */
  | 'dealDelete'
  /** Piece 32 part b (decision 0791): a message about this record, from the mailbox its module's rule names. */
  | 'sendEmail'
  /** 🔒 Piece 34, decision 1468: Raise a complaint, on the signed-in person's own Person page, for their own employment. */
  | 'personRaiseComplaint'
  /** 🔒 Piece 16, decision 12660: Look Up Manufacturer on a Good's page, for a writer of the Item. */
  | 'itemLookUp';

/** The facts a record page answers: a loose map, which is what the page itself reads. */
export type RecordPageFacts = Readonly<Record<string, string | boolean | null>>;

export interface RecordHeaderAction {
  key: RecordActionKey;
  /**
   * Where the action is drawn. `row` is the banner's own action row; `beforeTags`
   * is the same row ahead of the record's tag chips, which is where the Company's
   * Statements has always stood; `more` is the overflow the type declares;
   * `below` is a row of its own beneath both. Absent is the action row.
   */
  group?: 'beforeTags' | 'row' | 'more' | 'below';
  /** Whether this page offers it, read off the page's own answer; absent is always. */
  when?: (facts: RecordPageFacts) => boolean;
}

/**
 * 🔒 A staged record is one custom layout rather than the tile canvas (decision
 * 0293; figures 81 and 82): the tiles named in `before` read above the cards, the
 * ladder stands in a panel beside them, and the rest of the tiles follow.
 */
export interface RecordStagedLayout {
  /** 🔒 The tile keys drawn above the stage cards, in order (decision 0595). */
  before: readonly string[];
  /**
   * 🔒 Where a type names them, the page reads as bands top to bottom in place of
   * `before` and the flow after the cards (decision 1215): each entry is one band
   * of tiles drawn at one height, or `stages` for the stack of cards. A tile a
   * band names that the answer does not carry is left out, so a band of two with
   * one missing gives the other the full width.
   */
  bands?: readonly (readonly string[] | 'stages')[];
  /**
   * 🔒 One tile the page draws out of several the answer carries (decision 1217):
   * by the new tile's key, its title and the tiles it gathers, in section order.
   * Each gathered tile is a section with its count, the empty ones share one
   * closing sentence, and the first gathered tile's action is the merged tile's.
   */
  merges?: Readonly<Record<string, { titleKey: StringKey; from: readonly string[] }>>;
  /** The tile keys the layout draws somewhere other than the flow beneath the cards. */
  elsewhere: readonly string[];
  /** What the stack of cards is called, which is also the tile key it stands in for. */
  stagesKey: string;
  /** The cards themselves. */
  stages: RecordPanelKey;
  /** 🔒 A page-level banner at the head of the main column, under the rail on a phone (decision 1219): the Deal's Next Action. */
  notice?: RecordPanelKey;
  /**
   * 🔒 Below the desktop breakpoint the stage cards come straight after the
   * banner, the current card first and the finished ones after it, and the bands
   * follow (decision 1215). At desktop width the bands' own order stands.
   */
  phoneCurrentFirst?: boolean;
  /** The ladder's own heading, its panel and the rail that replaces the panel when it is folded. */
  panelTitleKey: StringKey;
  panel: RecordPanelKey;
  rail: RecordPanelKey;
}

/**
 * 🔒 Where a record type's page is one of the two document views rather than the
 * tile canvas alone (decision 0612; T181): the sheet or the details, the corner
 * ribbon, the one action bar on the floor and the chevron that saves the sheet.
 * 🔨 The record's own tiles are what the Details view draws, so a record that is
 * also a document keeps its notes, its history and its events.
 */
export interface RecordDocumentPage {
  /** The document's own type, as the projection, the registries and the suites name it. */
  documentType: string;
  /** The tile whose rows are the identifying facts at the top of the Details view (T170). */
  factsTile: string;
  /** The API root the printed sheet is saved from. */
  pdfBase: string;
  /** The attribute the page's footer has always carried, spelled as it has always been spelled. */
  footerAttribute: string;
  /** The fact the corner ribbon reads, in capitals. */
  ribbonFact: string;
  /** The footer's own controls, which the one action bar lays out. */
  footer: RecordPanelKey;
}

/** Which person a new event raised from this page starts with, beside its owner (product owner, 2026-09-11). */
export type RecordEventPerson = 'self' | 'linkedContact' | null;

/**
 * The person a new event from this page is associated with: the Person itself, or
 * the Lead's linked contact. 🔨 A page whose entry names neither associates
 * nobody, rather than guessing from a fact that happens to be there.
 */
export function associatedPersonFor(spec: RecordPageSpec, recordId: string, title: string, facts: RecordPageFacts): { personId: string; name: string } | null {
  if (spec.eventPerson === 'self') return { personId: recordId, name: title };
  if (spec.eventPerson !== 'linkedContact') return null;
  if (typeof facts.personId !== 'string' || !facts.personId) return null;
  return { personId: facts.personId, name: typeof facts.personName === 'string' ? facts.personName : facts.personId };
}

export interface RecordPageSpec {
  /**
   * The modals this type's page mounts, in the order it mounts them. The record
   * page mounts the ones every type carries — the edit form, a note, a tag, the
   * custom fields, a file's details, an upload, a share and an event — itself.
   */
  modals: readonly RecordModalKey[];
  /**
   * 🔒 Whether the page's banner offers + Log Contact (decision 0185). A party's
   * page does; an Item and a Location are things, and a contact logged against
   * one is a contact logged against nobody (decision 0280).
   */
  logsContact: boolean;
  /**
   * The panel drawn beside a tile's rows, by the tile's own key. The Item's image
   * stands beside its General Information fields and is the only one today.
   */
  tileAside: Readonly<Record<string, RecordPanelKey>>;
  /**
   * The panel drawn beneath a tile's own rows, by the tile's key. The tile keeps
   * its rows and the panel hangs under them, which is where Verify address sits
   * on a Location (decision 0338).
   */
  tileUnder: Readonly<Record<string, RecordPanelKey>>;
  /**
   * The construction drawn in place of a tile's item list, by the tile's key. A
   * tile named here draws itself and the Tile's rows are not used at all.
   */
  tilePanels: Readonly<Record<string, RecordPanelKey>>;
  /** The actions the banner offers, in the order it draws them. */
  headerActions: readonly RecordHeaderAction[];
  /**
   * 🔒 The overflow the banner's extra actions sit behind (decision 0277), where
   * the type declares one. A banner of nine identical buttons is what it avoids.
   */
  more: null | { labelKey: StringKey; attribute: string };
  /** Which person a new event from this page starts with beside its owner. */
  eventPerson: RecordEventPerson;
  /**
   * 🔒 What the banner's picture slot holds and what a change to it writes: a
   * person's photograph, a company's logo, a Location's photograph (decision
   * 0667), or nothing at all (T170; decision 0616). A record type naming neither draws its own glyph and offers no badge.
   */
  picture: 'person' | 'company' | 'location' | null;
  /**
   * Which end of an employment this page is (decision 0589). The Contacts tile
   * and the Employment tile list the same rows from opposite sides, so the window
   * that corrects one is mounted on both and needs to know which side asked.
   */
  employmentOrigin: 'company' | 'person' | null;
  /** The custom layout this record type draws instead of the tile canvas, where it has one. */
  staged: RecordStagedLayout | null;
  /** Whether the page's read pages its History a month at a time (piece 19). */
  pagesHistory: boolean;
  /** Which record a + New Quote raised here is carried to the quote form against. */
  quoteFrom: 'deal' | 'company';
  /**
   * The field the edit form takes the page's own title as, where the title is a
   * value the form writes. A Company's display name is the one of them.
   */
  editTitleKey: string | null;
  /** The document views this record type's page draws, where it is a document (T181). */
  documentPage: RecordDocumentPage | null;
  /**
   * 🔒 The summary column the banner draws between the name and the status pill
   * (decision 0665, amending 0621). 🔨 It is named here or it is not drawn: the
   * Page Module takes it from the registry and no page passes one of its own. The
   * Deal names its value and Next Action, and every other type names none.
   */
  bannerColumn: RecordPanelKey | null;
  /**
   * 🔒 One read the whole page shares, where the type has one (piece 22). The
   * Work Order's options are read by its lines panel and by its footer, and each
   * held its own until the page asked once between them.
   */
  sharedRead: 'workOrderOptions' | null;
  /**
   * 🔒 The tiles whose controls write through the record's own routes, and so are
   * drawn only where the answer's `editsRecord` is true (decision 1000; T365). The
   * Deal's team and contacts are written under Deal write; a note, a file or a tag
   * goes through its own route and gate, and is not named here.
   */
  writeTiles?: readonly string[];
  /**
   * 🔒 The settings that govern this page, which a gear at the right end of its
   * toolbar row opens (decision 2805; T1614). The Work Order and the Deal name
   * theirs; every other type names none and draws no gear.
   */
  settingsEntries?: readonly PageSettingsEntry[];
}

/**
 * 🔒 Whether the answer says this viewer takes the record's own writes (T355,
 * T365). An answer that says nothing is read as a writer's, as every page was
 * before the fact existed.
 */
export function editsRecordOf(facts: RecordPageFacts): boolean {
  return facts.editsRecord !== false;
}

/**
 * The query a + New Quote raised from this page carries to the quote's own page
 * (decision 0409). 🔨 A quote is created on its own page and never in a modal, so
 * the record it was raised from travels in the address.
 */
export function newQuoteQuery(spec: RecordPageSpec, recordId: string, facts: RecordPageFacts): string {
  if (spec.quoteFrom === 'deal') return `dealId=${encodeURIComponent(recordId)}`;
  const companyId = typeof facts.companyId === 'string' && facts.companyId ? facts.companyId : recordId;
  return `companyId=${encodeURIComponent(companyId)}`;
}

/**
 * 🔨 A record type whose page is not the tile canvas carries the empty spec
 * rather than being left out. An accounting document is drawn by the one
 * document view page and a stock document by its own (decisions 0409, 0441,
 * 0612), so there is no per-type fact of the record page to declare — and
 * `Record<PagedRecord, RecordPageSpec>` is what makes a new record type say so
 * rather than silently drawing nothing.
 */
const NO_RECORD_PAGE: RecordPageSpec = { modals: [], logsContact: false, tileAside: {}, tileUnder: {}, tilePanels: {}, headerActions: [], more: null, eventPerson: null, picture: null, employmentOrigin: null, staged: null, pagesHistory: false, quoteFrom: 'company', editTitleKey: null, documentPage: null, bannerColumn: null, sharedRead: null };

export const RECORD_PAGE_SPECS: Readonly<Record<PagedRecord, RecordPageSpec>> = {
  /**
   * The Company page (pieces 10 and 13): nine windows, the Vendor Tile behind the
   * Is Vendor flag, and the two banner moves that are its own.
   */
  CompanyRecord: {
    modals: [
      'recordPicture', 'companyNda', 'companyAccountStanding', 'employmentEdit', 'siteOccupancy',
      'employmentAdd', 'companySite', 'companyAccountRole', 'companyVendorRep', 'companyTermsSet', 'companyMailbox', 'companyOwnership',
      // 🔒 Decision 11480: the one Release window, which a Company file's row opens as a Deal file's does.
      'releaseFile',
    ],
    logsContact: true,
    tileAside: {},
    tileUnder: {},
    tilePanels: { vendor: 'vendorTax', changeRequests: 'companyChangeRequests', fieldService: 'companyJobMessages', suggestions: 'recordSuggestions' },
    headerActions: [
      /* Piece 27, decision 0223: a customer's statement for a period, computed live and snapshotted only when it is sent. */
      /* 🔒 Decision 0971: neither move is drawn where the answer says this viewer writes nothing on the record. */
      { key: 'companyStatements', group: 'beforeTags', when: (facts) => facts.isCustomer === true && facts.writesRecord !== false },
      /* 🔨 + New Deal from the Account itself, never + New Quote (decision 0497): work enters at the Deal. */
      { key: 'companyNewDeal', when: (facts) => facts.isOwnCompany !== true && facts.writesRecord !== false },
      /* 🔒 Decision 0971: an outside login's own company page carries Request change, from the answer's own fact. */
      { key: 'companyRequestChange', when: (facts) => facts.requestsChange === true },
    ],
    more: null,
    eventPerson: null,
    picture: 'company',
    employmentOrigin: 'company',
    staged: null,
    pagesHistory: false,
    quoteFrom: 'company',
    /** The account's display name is the banner's title and the edit form writes it. */
    editTitleKey: 'displayName',
    documentPage: null,
    bannerColumn: null,
    sharedRead: null,
  },
  /**
   * The Person page (piece 13): the photograph, the employment window from the
   * person's end, a new employment, the skills and the contact methods.
   */
  PersonRecord: {
    modals: ['recordPicture', 'employmentEdit', 'employmentAdd', 'personSkill', 'personContactMethod', 'personEducation', 'personConfidentialityGrant'],
    logsContact: true,
    tileAside: {},
    tileUnder: {},
    tilePanels: { texts: 'personTexts', suggestions: 'recordSuggestions', education: 'personEducation', otherSkills: 'personOtherSkills', profile: 'personProfile', confidentialityRoles: 'personConfidentialityRoles' },
    // 🔒 Decision 1468: offered on the page of the person signed in, while they hold an active employment at our own company.
    headerActions: [{ key: 'personRaiseComplaint', when: (facts) => facts.raisesComplaint === true }],
    more: null,
    /** A new event from a Person's page is associated with that person (product owner, 2026-09-11). */
    eventPerson: 'self',
    picture: 'person',
    employmentOrigin: 'person',
    staged: null,
    pagesHistory: false,
    quoteFrom: 'company',
    editTitleKey: null,
    documentPage: null,
    bannerColumn: null,
    sharedRead: null,
  },
  /**
   * The Location page: an occupancy corrected from its Occupants tile, + Add
   * Occupant, and Verify address under its own facts (decisions 0338, 0589).
   * 🔨 A Location is a building, so nothing is contacted there (decision 0280).
   */
  LocationRecord: {
    // 🔒 Decision 11480: a Location file's row opens the one Release window, listing the occupants' companies.
    modals: ['recordPicture', 'siteOccupancy', 'locationOccupant', 'releaseFile'],
    logsContact: false,
    tileAside: {},
    tileUnder: { generalInformation: 'locationVerify' },
    tilePanels: {},
    headerActions: [],
    more: null,
    eventPerson: null,
    picture: 'location',
    employmentOrigin: null,
    staged: null,
    pagesHistory: false,
    quoteFrom: 'company',
    editTitleKey: null,
    documentPage: null,
    bannerColumn: null,
    sharedRead: null,
  },
  /**
   * The Site page's five windows: a certificate, a requirement, a dated role,
   * the window that ends a requirement on a named day (decisions 0295, 0309)
   * and the one that ends a certificate (decision 0676). The Exempt Customer
   * Steps window, which carries the certificate request links under its steps
   * (decisions 0679, 2851), opens from the certificates tile's header.
   */
  SiteRecord: {
    modals: ['siteCertificate', 'siteRequirement', 'siteRole', 'siteRequirementEnd', 'siteCertificateEnd', 'siteCertificateRemove', 'siteCertificateMove', 'siteExemptSteps'],
    logsContact: true,
    tileAside: {},
    tileUnder: {},
    tilePanels: { taxJurisdiction: 'siteTaxJurisdiction', assets: 'siteAssets', plantFeed: 'sitePlantFeed' },
    headerActions: [],
    more: null,
    eventPerson: null,
    picture: null,
    employmentOrigin: null,
    staged: null,
    pagesHistory: false,
    quoteFrom: 'company',
    editTitleKey: null,
    documentPage: null,
    bannerColumn: null,
    sharedRead: null,
  },
  /**
   * The Item page's four windows, and its image beside its fields (piece 17).
   * 🔨 An Item is a thing, so its page logs no contact (decision 0280).
   */
  Item: {
    modals: ['itemAttribute', 'itemSupplier', 'itemSiteStock', 'itemImage', 'itemIssueStock'],
    logsContact: false,
    tileAside: { generalInformation: 'itemImage' },
    // 🔒 Issue Stock hangs under the Stock by Site rows, beside the tile's own + Stock at site (decision 1142).
    tileUnder: { stockBySite: 'itemIssueStock' },
    tilePanels: { transactions: 'itemTransactions' },
    // 🔒 Decision 12660: Look Up Manufacturer on a Good, for a writer of the Item; a service has no maker to look up.
    headerActions: [{ key: 'itemLookUp', when: (facts) => editsRecordOf(facts) && facts.type === 'Good' }],
    more: null,
    eventPerson: null,
    picture: null,
    employmentOrigin: null,
    staged: null,
    pagesHistory: false,
    quoteFrom: 'company',
    editTitleKey: null,
    documentPage: null,
    bannerColumn: null,
    sharedRead: null,
  },
  /**
   * The Deal page (piece 19): the staged layout, its five windows, the ladder and
   * the current stage's card, and the floor working under the Deal Team, where
   * General Information stood until decision 0722 took that tile away.
   */
  Deal: {
    modals: ['dealLost', 'dealTeam', 'dealOwner', 'dealContact', 'dealRfq', 'releaseFile', 'dealClone', 'dealDelete'],
    logsContact: true,
    tileAside: {},
    /* The floor working hangs under the Deal Team for a super admin alone (decision 0722); the route refuses anybody else.
       🔒 Earlier Month stands at the foot of History, the tile it pages, not in the banner's action row (decision 1215). */
    tileUnder: { dealTeam: 'dealFloorWorking', history: 'dealHistoryFoot', workOrders: 'dealJobMessages' },
    tilePanels: { dealProgress: 'dealProgress', stageCard: 'dealStageCard' },
    headerActions: [
      /* 🔨 No Won button here and no Mark Lost at Decision (decision 0595): the stage that ends the
         deal carries both on its own card. Mark Lost stays in the banner before Decision, because a
         Deal is lost at any stage and the earlier cards are not where that is decided. */
      /* 🔒 Each move writes through a route asking Deal write, so it is drawn only where the answer says the viewer takes it (decision 1000; T365). */
      { key: 'dealLost', when: (facts) => editsRecordOf(facts) && facts.isLost !== true && facts.isWon !== true && facts.atDecision !== true },
      { key: 'dealReopen', when: (facts) => editsRecordOf(facts) && facts.isLost === true },
      { key: 'dealApprove', when: (facts) => editsRecordOf(facts) && facts.quickApprovalHeld === true && facts.quickApprovalMayAnswer !== false },
      { key: 'dealReject', when: (facts) => editsRecordOf(facts) && facts.quickApprovalHeld === true && facts.quickApprovalMayAnswer !== false },
      /* 🔒 Clone stands in every stage, Lost and Won included, and makes a Deal, so it asks what a create asks (decision 1482).
         🔒 It is drawn only where the answer's `mayClone` says the clone route takes this viewer on this Deal, and an answer
         that says nothing draws none (decision 1488; T757).
         🔨 It stands on this row and not in the title bar beside Edit, which holds one action (decision 1440). */
      { key: 'dealClone', when: (facts) => editsRecordOf(facts) && facts.mayClone === true },
      /* The gate notice draws only where the address names a gate, which the page reads and the facts do not carry. */
      { key: 'dealGate' },
      /* Piece 32 part b (decision 0791): mail about the Deal goes from the mailbox the Deal module's rule names. */
      { key: 'sendEmail' },
      /* 🔒 The Deal Pack stands under More for every reader the page admits, because its routes ask the page's own read (decision 5400). */
      { key: 'dealCopyForClaude', group: 'more' },
      { key: 'dealDownloadPack', group: 'more' },
      /* 🔒 Delete Deal under More, drawn from the write the delete route asks; its window reads what holds the Deal (decision 12700). */
      { key: 'dealDelete', group: 'more', when: (facts) => editsRecordOf(facts) },
    ],
    more: { labelKey: 'deal.action.more', attribute: 'data-deal-more' },
    eventPerson: null,
    picture: null,
    employmentOrigin: null,
    staged: {
      /**
       * 🔒 What stands above the stage cards (decisions 0595, 0722). The Deal's team
       * takes the first slot, because it is who is working the deal; General
       * Information stood above it until its rows each found a place of their own.
       * Product owner, 2026-09-20: *"I think deal team should be at top under the
       * general information."* 🔨 The bands below name every tile since decision
       * 1215, the team first among them, so nothing stands in `before`.
       */
      before: [],
      /**
       * 🔒 Six bands under the banner, each two-tile band at one height (decision
       * 1215): the people, the stages, the documents, the files beside the events,
       * the notes beside the custom fields, and History. The owner, 2026-09-27:
       * *"the layout is choppy and could be cleaned up to flow better"*.
       */
      bands: [
        ['dealTeam', 'dealContacts'],
        'stages',
        ['dealDocuments'],
        ['dealFiles', 'upcomingEvents'],
        ['internalNotes', 'customFields'],
        ['history'],
      ],
      /* 🔒 The five document lists are one Documents tile, a section per kind (decision 1217); Files stays its own tile, because a file carries Release. */
      merges: { dealDocuments: { titleKey: 'deal.documents.title', from: ['quotes', 'salesOrders', 'invoices', 'purchaseOrders', 'workOrders'] } },
      /* 🔨 No Next Action tile is drawn (decision 1215): its rows are the ladder's open sub-stages, each with its Due. */
      elsewhere: ['dealProgress', 'stageCard', 'nextAction', 'quotes', 'salesOrders', 'invoices', 'purchaseOrders', 'workOrders'],
      stagesKey: 'stageCard',
      stages: 'dealStages',
      /* 🔒 Next Action is the page's own banner, shown on every open until dismissed (decision 1219). */
      notice: 'dealNextAction',
      /* 🔒 On a phone the current card comes straight after the banner, because a rep on a phone is usually ticking something off (decision 1215). */
      phoneCurrentFirst: true,
      panelTitleKey: 'deal.progress.panel',
      panel: 'dealProgress',
      rail: 'dealRail',
    },
    pagesHistory: true,
    quoteFrom: 'deal',
    editTitleKey: null,
    documentPage: null,
    bannerColumn: 'dealBannerColumn',
    sharedRead: null,
    writeTiles: ['dealTeam', 'dealContacts'],
    /** 🔒 The gear leads to Stage Actions and Stage Questions (decision 2805). */
    settingsEntries: PAGE_SETTINGS_ENTRIES.deal,
  },
  /**
   * The Lead page (piece 20): the three StageCards drawing themselves, the four
   * dialogs, and the banner's own moves with the rest behind More (decision 0277).
   */
  Lead: {
    modals: ['leadNotFit', 'leadConvertDeal', 'leadRedirect', 'leadDispatch'],
    logsContact: true,
    tileAside: {},
    tileUnder: {},
    tilePanels: { leadStageCards: 'leadStageCards' },
    headerActions: [
      /* 🔒 Every Lead move writes under Deal write, the Lead's module, so none is drawn where the answer refuses it (decision 1000; T365). */
      { key: 'leadCallAttempt', when: (facts) => editsRecordOf(facts) && facts.isOpen === true },
      { key: 'leadConvertDeal', when: (facts) => editsRecordOf(facts) && facts.canConvertToDeal === true },
      { key: 'leadConvertCompany', when: (facts) => editsRecordOf(facts) && facts.canConvertToCompany === true },
      { key: 'leadRelease', when: (facts) => editsRecordOf(facts) && facts.isHeld === true },
      { key: 'leadHot', group: 'more', when: (facts) => editsRecordOf(facts) && facts.isOpen === true && facts.temperature !== 'Hot' },
      { key: 'leadNotFit', group: 'more', when: (facts) => editsRecordOf(facts) && facts.isOpen === true },
      { key: 'leadRedirect', group: 'more', when: (facts) => editsRecordOf(facts) && facts.isOpen === true },
      { key: 'leadDispatch', group: 'more', when: (facts) => editsRecordOf(facts) && facts.isOpen === true },
      { key: 'leadBlock', group: 'more', when: (facts) => editsRecordOf(facts) && (facts.isHeld === true || facts.status === 'New' || facts.status === 'Working') },
      /* 🔨 The suggested match sits beside the submitted text with Link to this and Create new; nothing resolves itself. */
      { key: 'leadSuggestedMatch', group: 'below', when: (facts) => editsRecordOf(facts) && Boolean(facts.suggestedCompanyId || facts.suggestedPersonId) && facts.isOpen === true },
    ],
    more: { labelKey: 'lead.action.more', attribute: 'data-lead-more' },
    /** A new event from a Lead's page is associated with the contact the Lead names (product owner, 2026-09-11). */
    eventPerson: 'linkedContact',
    picture: null,
    employmentOrigin: null,
    staged: {
      before: ['generalInformation'],
      elsewhere: ['generalInformation', 'leadStageCards'],
      stagesKey: 'leadStageCards',
      stages: 'leadStageCards',
      panelTitleKey: 'lead.progress.panel',
      panel: 'leadProgress',
      rail: 'leadRail',
    },
    pagesHistory: false,
    quoteFrom: 'company',
    editTitleKey: null,
    documentPage: null,
    bannerColumn: null,
    sharedRead: null,
  },
  /**
   * 🔒 The Work Order is the tenth document page (T181, on decision 0612). It is
   * a record with tiles and a document with a sheet, so it draws the two views
   * every other accounting document draws and its own tiles are the Details view.
   */
  WorkOrder: {
    modals: [],
    logsContact: true,
    tileAside: {},
    tileUnder: {},
    /* The job's three constructions (piece 22): the labour lines with their workers as chips, the parts fitted and the travel line. */
    tilePanels: { progress: 'workOrderProgress', lines: 'workOrderLines', materials: 'workOrderMaterials', travel: 'workOrderTravel', dealTeam: 'workOrderDealTeam', visits: 'workOrderVisits', labour: 'workOrderLabour', documents: 'workOrderDocuments' },
    /* Piece 32 part b (decision 0791): a work order's mail goes from the mailbox the Work Order module's rule names. */
    headerActions: [{ key: 'sendEmail' }],
    more: null,
    eventPerson: null,
    picture: null,
    employmentOrigin: null,
    staged: null,
    pagesHistory: false,
    quoteFrom: 'company',
    editTitleKey: null,
    documentPage: {
      documentType: 'WorkOrder',
      factsTile: 'generalInformation',
      pdfBase: '/work-orders',
      footerAttribute: 'data-work-order-footer',
      ribbonFact: 'status',
      footer: 'workOrderFooter',
    },
    bannerColumn: null,
    sharedRead: 'workOrderOptions',
    /** 🔒 The gear leads to Priority Tiers and to Checklist Templates on the Knowledge section (decisions 2805, 2281). */
    settingsEntries: PAGE_SETTINGS_ENTRIES.workOrder,
  },
  PurchaseOrder: NO_RECORD_PAGE,
  RFQ: NO_RECORD_PAGE,
  // 🔒 Decision 2910: a Service Report's page is its visit's page under the job, so its record route draws no tile canvas of its own.
  ServiceReport: NO_RECORD_PAGE,
  BOM: NO_RECORD_PAGE,
  Quote: NO_RECORD_PAGE,
  SalesOrder: NO_RECORD_PAGE,
  Invoice: NO_RECORD_PAGE,
  CreditNote: NO_RECORD_PAGE,
  Payment: NO_RECORD_PAGE,
  Bill: NO_RECORD_PAGE,
  VendorCredit: NO_RECORD_PAGE,
  StockCount: NO_RECORD_PAGE,
  StockTransfer: NO_RECORD_PAGE,
  StockRevaluation: NO_RECORD_PAGE,
  // Decision 0761: the Manual Journal draws its own document page, not a tile canvas.
  JournalEntry: NO_RECORD_PAGE,
};
